<template>
  <div id="app-container">
    <component :is="resolveLayout">
      <router-view></router-view>
    </component>

    <NativeNotificationPrompt
      v-model="promptVisible"
      :activation-fn="activationFn"
    />
  </div>
</template>

<script>
import axios from 'axios'
import store from './store'
import { computed, watch, ref } from '@vue/composition-api'
import { useRouter } from '@/utils'
import LayoutBlank from '@/layouts/Blank.vue'
import LayoutContent from '@/layouts/Content.vue'
import NativeNotificationPrompt from '@/components/NativeNotificationPrompt.vue'

export default {
  components: {
    LayoutBlank,
    LayoutContent,
    NativeNotificationPrompt,
  },
  setup(props, context) {
    const { route } = useRouter()
    store.commit('initialiseStore')

    let token = store.state.token
    if (token) {
      axios.defaults.headers.common['Authorization'] = 'Token ' + token
    } else {
      axios.defaults.headers.common['Authorization'] = ''
    }

    const resolveLayout = computed(() => {
      if (route.value.name === null) return null
      if (route.value.meta.layout === 'blank') return 'layout-blank'
      return 'layout-content'
    })

    const isAuthenticated = computed(() => store.state.isAuthenticated)

    // ✅ Statut backend (source de vérité)
    const backendSubscribed = ref(false)

    // ── Appel API pour obtenir le statut de souscription ──────────
    const fetchSubscriptionStatus = async () => {
      if (!isAuthenticated.value) return
      try {
        const response = await axios.get('/api/v1/webpush/status/', {
          params: { group: 'managers' },
          headers: { Authorization: `Token ${store.state.token}` }
        })
        backendSubscribed.value = response.data.subscribed === true
        console.log('[WebPush Manager] Statut backend:', backendSubscribed.value)
      } catch (error) {
        console.warn('Erreur lors de la récupération du statut WebPush (Manager):', error)
        backendSubscribed.value = false
      }
    }

    // ── Fonction d'activation (appelle _triggerWebPushPermission) ──
    const activationFn = async () => {
      if (typeof window._triggerWebPushPermission === 'function') {
        const routerInstance = context.root.$router
        const success = await window._triggerWebPushPermission(routerInstance)
        if (success) {
          const userId = store.getters.userId
          if (userId) {
            localStorage.setItem(`webpush_subscribed_${userId}`, 'true')
          }
          // ✅ Mettre à jour le statut backend localement
          backendSubscribed.value = true
        }
        return success
      } else {
        console.error('❌ _triggerWebPushPermission non disponible')
        return false
      }
    }

    // ── Décide si la bannière doit s'afficher ──────────────────────
    const shouldShowPrompt = () => {
      if (typeof Notification === 'undefined') return false
      if (Notification.permission === 'denied') return false
      if (!isAuthenticated.value) return false

      // 🔹 Priorité au statut backend
      if (backendSubscribed.value) return false

      // 🔹 Fallback sur localStorage
      const userId = store.getters.userId
      if (userId && userId !== 'anonymous') {
        const flagKey = `webpush_subscribed_${userId}`
        if (localStorage.getItem(flagKey) === 'true') return false
      }

      return true
    }

    // ── Visibilité de la bannière ──────────────────────────────────
    const promptVisible = ref(false)

    const updatePromptVisibility = () => {
      const shouldShow = shouldShowPrompt()
      if (shouldShow && !promptVisible.value) {
        setTimeout(() => {
          promptVisible.value = true
        }, 500)
      } else if (!shouldShow) {
        promptVisible.value = false
      }
    }

    // ── Check des collectes dues (silencieux) ──────────────────────
    const checkCollectionNotifications = async () => {
      if (!isAuthenticated.value) return
      try {
        await axios.get('/api/v1/manager/njangi/check-collection-notifications/')
      } catch (e) {
        // silent check
      }
    }

    // ── Watcher combiné : isAuthenticated + userId ──────────────────
    watch([isAuthenticated, () => store.getters.userId], async () => {
      if (isAuthenticated.value) {
        await fetchSubscriptionStatus()
        updatePromptVisibility()
        checkCollectionNotifications()
      } else {
        promptVisible.value = false
      }
    }, { immediate: true })

    return {
      resolveLayout,
      isAuthenticated,
      activationFn,
      promptVisible,
    }
  }
}
</script>
